import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { LidarZoneSelectionModel } from "./types/laserScanScene";
import { LaserScanParameterModel } from "~/pages/copilotMessage/types/copilot";
import { CurrentProcessedThermalImageDataModel } from "../irScanAnalysis/hooks/useGetProcessedThermalImages";
import { useGetFilePath } from "~/hooks/useGetFilePath";
import { usePrepareProcessedLidarZoneSectionData } from "./hooks/usePrepareProcessedLidarZoneSectionData";
import { Progress, Switch } from "antd";
import styles from "./LaserScanScene.module.less";
import { useLanguage } from "~/hooks/useLanguage";
import { useLaserScanScene } from "./providers/LaserScanSceneProvider";
import { OrbitControls } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { LinearToneMapping, LinearSRGBColorSpace } from "three";
import LaserMesh from "./components/laserMesh/LaserMesh";
import IRShellMesh from "./components/irShellMesh/IRShellMesh";
interface LaserScanSceneProps {
  lidarParams: LaserScanParameterModel;
  lidarZoneSectionData: LidarZoneSelectionModel[];
  processedThermalImages: CurrentProcessedThermalImageDataModel | undefined;
  isExpanded: boolean;
}

const LaserScanScene: FC<LaserScanSceneProps> = ({
  lidarParams,
  lidarZoneSectionData,
  processedThermalImages,
  isExpanded,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tooltipRef = useRef<HTMLSpanElement | null>(null);

  const clearTooltip = useCallback(() => {
    if (tooltipRef.current && canvasRef.current) {
      tooltipRef.current.style.display = "none";
      canvasRef.current.style.cursor = "default";
    }
  }, []);

  const heatId = useMemo(() => {
    return lidarParams.CampaignHeatId.toString() + "|" + Date.now().toString();
  }, [lidarParams]);

  const filePath = useGetFilePath(
    lidarParams.CalculatedLiDARMeasurementsCloudFileKey,
  );

  const { formatMessage } = useLanguage();

  const {
    isDisplaySwitchDisabled,
    isDisplayIRShell,
    handleIRShellDisplay,
    handleSwitchDisable,
  } = useLaserScanScene();

  const { loadingState } = usePrepareProcessedLidarZoneSectionData(
    filePath,
    heatId,
    lidarZoneSectionData,
    processedThermalImages,
  );

  useEffect(() => {
    if (processedThermalImages) {
      handleIRShellDisplay(true);
    } else {
      handleSwitchDisable(true);
    }
  }, [handleIRShellDisplay, handleSwitchDisable, processedThermalImages]);

  return (
    <>
      <div className={styles.container}>
        <span className={styles.tooltip} ref={tooltipRef}></span>
        {loadingState < 100 && (
          <div className={styles.spinnerContainer}>
            <Progress percent={loadingState} showInfo={false} />
          </div>
        )}
        <Canvas
          key={isExpanded ? "expanded" : "collapsed"}
          camera={{
            fov: 50,
            near: 0.1,
            far: 500,
            position: [0, 0, 35],
          }}
          style={{ background: "#dedef0" }}
          gl={{
            outputColorSpace: LinearSRGBColorSpace,
            toneMapping: LinearToneMapping,
          }}
          ref={canvasRef}
          frameloop="demand"
          onMouseOut={clearTooltip}
          onPointerOut={clearTooltip}
        >
          <ambientLight intensity={0.2} />
          <spotLight
            position={[0, 0, 30]}
            angle={0.2}
            color={"#ffffff"}
            intensity={4000}
          />
          <spotLight
            position={[0, 30, -20]}
            color={"#ffffff"}
            intensity={2000}
          />
          <spotLight
            position={[0, -30, -20]}
            color={"#ffffff"}
            intensity={2000}
          />
          <spotLight
            position={[-30, 0, -20]}
            color={"#ffffff"}
            intensity={2000}
          />
          <spotLight
            position={[30, 0, -20]}
            color={"#ffffff"}
            intensity={2000}
          />
          <LaserMesh
            isExpanded={isExpanded}
            tooltipRef={tooltipRef}
            canvasRef={canvasRef}
            clearTooltip={clearTooltip}
          />

          {isDisplayIRShell && (
            <IRShellMesh
              canvasRef={canvasRef}
              tooltipRef={tooltipRef}
              clearTooltip={clearTooltip}
              isExpanded={isExpanded}
            />
          )}

          <OrbitControls makeDefault enableZoom={true} enableRotate={true} />
        </Canvas>
      </div>

      <Switch
        className={styles.irLayerSlider}
        checkedChildren={formatMessage("LaserScanAnalysis.3dMap.IROverlay.On")}
        unCheckedChildren={formatMessage(
          "LaserScanAnalysis.3dMap.IROverlay.Off",
        )}
        disabled={isDisplaySwitchDisabled}
        checked={isDisplayIRShell}
        onChange={(val) => {
          handleIRShellDisplay(val);
        }}
      />
    </>
  );
};

export default LaserScanScene;
